// Badge tone + label helpers for the ticket taxonomy, mirroring the design's
// portal-data.jsx helpers.
import type { Tone } from "../ds/DS";

export const TICKET_CATEGORIES = ["Issue", "Question", "Change"];
// Portal overhaul O1: four statuses (migration 0029 remapped the old six).
export const BOARD_STATUSES = [
  "Open",
  "In progress",
  "Waiting on client",
  "Closed",
];
// Columns shown on the admin board (one per board status).
export const BOARD_COLUMNS = BOARD_STATUSES;
export const PRIORITIES = ["Low", "Normal", "High", "Urgent"];

export const statusTone = (s: string): Tone =>
  (({
    Open: "navy",
    "In progress": "teal",
    "Waiting on client": "warning",
    Closed: "neutral",
  }[s] as Tone) ?? "neutral");

/** Colour of the status dot (segmented control, board column heads). */
export const statusDot = (s: string): string =>
  ({
    Open: "var(--ink-navy)",
    "In progress": "var(--bright-teal)",
    "Waiting on client": "var(--status-warning)",
    Closed: "var(--border-strong)",
  })[s] ?? "var(--border-strong)";

/** Client-facing wording for each status. */
export const clientStatusLabel = (s: string): string =>
  ({
    Open: "Received",
    "In progress": "We're on it",
    "Waiting on client": "Waiting on you",
    Closed: "Closed",
  })[s] ?? s;

export const clientStatusTone = (s: string): Tone =>
  s === "In progress" ? "teal" : s === "Waiting on client" ? "warning" : "neutral";

export const typeTone = (t: string): Tone =>
  (({
    Issue: "warning",
    Question: "neutral",
    Change: "neutral",
  }[t] as Tone) ?? "neutral");

export const priorityTone = (p: string): Tone =>
  (({ Urgent: "danger", High: "warning", Normal: "neutral", Low: "neutral" }[
    p
  ] as Tone) ?? "neutral");

/** Compact age for list rows: "now" / "12m" / "3h" / "2d" / "12 Sep". */
export function shortAge(ms: number): string {
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

/** "2h ago" / "3d ago" / "12 Sep" from an epoch-ms timestamp. */
export function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
